import get from 'lodash/get';
import isNumber from 'lodash/isNumber';
import startCase from 'lodash/startCase';
import format from 'date-fns/format';
import * as ChartConstants from '../components/Home/Home/ChartConstants';
import * as Constants from '../data/Constants';
import * as Functions from './Functions';

const sumReducerFunc = (acc, val) => {
  const valueToAdd = val[1];
  if (!isNumber(acc) || !isNumber(valueToAdd)) return acc;
  return acc + valueToAdd;
};
const getTotal = dataArr => dataArr.reduce(sumReducerFunc, 0);

const isHomeAnalyticsRoute = route => ChartConstants.ORDERS_ANALYTICS_ROUTE_REGEX.test(route);
const isGetAppDownloadsRoute = route => ChartConstants.APP_DOWNLOADS_API_ROUTE_REGEX.test(route);
const isAHomeAnalyticsRoute = route => isHomeAnalyticsRoute(route) || isGetAppDownloadsRoute(route);

const getAnalyticsDatesQueryParams = (startDate, endDate) => {
  const queryParams = new URLSearchParams();
  const dateFormat = 'yyyy-MM-dd';
  if (startDate) {
    queryParams.append('start-date', format(startDate, dateFormat));
  }
  queryParams.append('end-date', format(endDate, dateFormat));

  return queryParams.toString();
};

const getBaseChartDataObject = () => ({
  labels: [],
  datasets: [],
});

const getBaseOneLineConfig = optionsObject => ({
  fill: false,
  lineTension: 0.1,
  backgroundColor: ChartConstants.DEFAULT_SINGLE_LINE_COLOR,
  borderColor: ChartConstants.DEFAULT_SINGLE_LINE_COLOR,
  borderCapStyle: 'butt',
  borderDash: [],
  borderDashOffset: 0.0,
  borderJoinStyle: 'miter',
  pointBorderColor: ChartConstants.DEFAULT_SINGLE_LINE_COLOR,
  pointBackgroundColor: ChartConstants.DEFAULT_SINGLE_LINE_COLOR,
  pointBorderWidth: [],
  pointHoverRadius: 5,
  pointHoverBackgroundColor: 'rgba(75,192,192,1)',
  pointHoverBorderColor: 'rgba(220,220,220,1)',
  pointHoverBorderWidth: 6,
  pointRadius: 1,
  pointHitRadius: 10,
  data: [],
  ...optionsObject,
});

const getBasePieConfigDatasetObject = () => ({
  data: [],
  backgroundColor: [],
});

const hasMultipleDataSets = (labels, rawData) => labels.length >= rawData.length;

const setLineDataPointPropertiesFromRaw = ({
  rawData,
  datasetObject,
  chartData,
}) => {
  const { label } = datasetObject;
  const pointWidth = get(ChartConstants.CHART_POINT_OPTIONS, `lineCharts.${label}.pointWidth`, 5);
  const dataPointValueEachDay = new Array();
  const widthEachPoint = new Array();

  if (!Array.isArray(rawData)) {
    datasetObject.data = dataPointValueEachDay;
    datasetObject.pointBorderWidth = widthEachPoint;
    chartData.datasets.push(datasetObject);

    return chartData;
  }

  rawData.forEach((item, i) => {
    const dateString = item[0];
    const pointValue = item[1];
    const dateParts = dateString.split('-');
    if (dateParts.length !== 3) {
      return;
    }
    // monthIndex is 0 based.
    const monthIndex = parseInt(dateParts[1]) - 1;
    const monthName = Constants.months[monthIndex];
    const dayOfMonth = dateParts[2];

    if (!hasMultipleDataSets(chartData.labels, rawData)) {
      chartData.labels.push(`${monthName} ${dayOfMonth}`);
    }

    widthEachPoint[i] = pointWidth;
    dataPointValueEachDay[i] = pointValue;
  });

  datasetObject.data = dataPointValueEachDay;
  datasetObject.pointBorderWidth = widthEachPoint;
  chartData.datasets.push(datasetObject);

  return chartData;
};

const setDeliveryOptionOrderInfoOnChartData = (deliveryOptionOrders, chartData, datasetObject) => {
  if (deliveryOptionOrders.length > 0) {
    deliveryOptionOrders.forEach((option, i) => {
      const [deliveryOption, orderCount] = option;
      const formattedDeliveryOption = startCase(deliveryOption);
      chartData.labels.push(formattedDeliveryOption);
      datasetObject.data.push(orderCount);
      datasetObject.backgroundColor.push(ChartConstants.PIE_DATA_AND_LINE_DATASET_COLORS[i + 1]);
    });
  }
  chartData.datasets.push(datasetObject);
  return chartData;
};

const setMultipleDatasetsOnOneLineChart = (datasets, chartData) => {
  if (!datasets) return chartData;

  Object.entries(datasets).forEach(([key, dataset]) => {
    const lineColor = get(ChartConstants.CHART_POINT_OPTIONS, `lineCharts.${key}.lineColor`, Constants.craverColors[0]);
    const pointColor = get(ChartConstants.CHART_POINT_OPTIONS, `lineCharts.${key}.pointColor`, Constants.craverColors[0]);
    const datasetOptionsConfig = {
      label: key,
      backgroundColor: lineColor,
      borderColor: lineColor,
      pointBackgroundColor: pointColor,
      pointBorderColor: pointColor,
    };
    const datasetObject = getBaseOneLineConfig(datasetOptionsConfig);
    const setLineDataPointPropertiesArgs = {
      rawData: dataset,
      datasetObject,
      chartData,
    };
    chartData = setLineDataPointPropertiesFromRaw(setLineDataPointPropertiesArgs);
  });
  return chartData;
};

function addCalculatedTitleStat(totalsToCalculate, timescaleData, title) {
  totalsToCalculate.forEach((item) => {
    const { responseLookupKey, format } = item;
    const responseData = get(timescaleData, responseLookupKey);
    if (!Array.isArray(responseData)) return 0;
    const total = getTotal(responseData);
    const formattedTotal = format === 'currency' ? `$ ${Functions.showTwoDecimalsForMoney(total)}` : total;
    const nextTitleSegment = ` - ${formattedTotal}`;
    title += nextTitleSegment;
  });
  return title;
}

export {
  getBaseChartDataObject,
  getBaseOneLineConfig,
  getBasePieConfigDatasetObject,
  hasMultipleDataSets,
  setLineDataPointPropertiesFromRaw,
  setDeliveryOptionOrderInfoOnChartData,
  getTotal,
  isHomeAnalyticsRoute,
  getAnalyticsDatesQueryParams,
  isGetAppDownloadsRoute,
  isAHomeAnalyticsRoute,
  setMultipleDatasetsOnOneLineChart,
  addCalculatedTitleStat,
};
